<script setup lang="ts">
import { ref, computed, type PropType, type Ref } from 'vue';
import type {
  SurveyPermittedAnswerModel,
  SurveyProgressAnswer,
  SurveyQuestionModel,
} from 'src/types/model';
import { useSurveyStore } from 'src/store';
import {QuestionCategoryResponseEnum, SurveyComponentTypeEnum} from 'src/constants/survey';
import Checkbox from 'src/components/form/Checkbox.vue';
import Radio from 'src/components/form/Radio.vue';
import InputWithValidation from 'src/components/form/InputWithValidation.vue';
import NumberInputWithValidation from 'src/components/form/NumberInputWithValidation.vue';
import SelectWithValidation from 'src/components/form/SelectWithValidation.vue';
import {useI18n} from 'vue-i18n';
import IconValNmSubtextBtnAsRadio from "./IconValNmSubtextBtnAsRadio.vue";

const {t}= useI18n();

const props = defineProps({
  depth: {
    type: Number,
    required: true
  },
  question: {
    type: Object as PropType<SurveyQuestionModel>,
    required: true
  },
  permittedAnswer: {
    type: Object as PropType<SurveyPermittedAnswerModel>,
    required: true
  },
});

const surveyModule = useSurveyStore();
const multipleChoiceSubAnswers = ref();
const syncValue = defineModel() as Ref<Record<string, unknown>>;

const inputRef = ref<InstanceType<typeof InputWithValidation> | null>(null);

const showOptions = computed(() => {
  const selectedValues = syncValue.value[props.depth] as Record<string, unknown>;
  if (props.permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.radio ||
      props.permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.iconRadio) {
    return selectedValues[`${props.permittedAnswer.renderingConfig.groupIdentifier}`] == props.permittedAnswer.answerId.toString();
  } else if (props.permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.checkbox) {
    return selectedValues[`${props.question.questionId}-${props.permittedAnswer.answerId}-checked`];
  }
  else if (props.permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.heading) {
    return true;
  }
});

const showSubText = computed(() => {
  return props.permittedAnswer.subText
    && props.permittedAnswer.renderingConfig.type !== SurveyComponentTypeEnum.iconRadio;
});

const value = computed(() => {
  let value;
  const selectedValues = syncValue.value[props.depth] as Record<string, unknown>;

  const radioValue =
    selectedValues[props.permittedAnswer.renderingConfig.groupIdentifier as string];

  const textValue = selectedValues[`${props.permittedAnswer.answerId}-text`];
  const checkedValue = selectedValues[`${props.question.questionId}-${props.permittedAnswer.answerId}-checked`];
  const numberValue = selectedValues[`${props.question.questionId}-number`];
  const dropdownValue = selectedValues[`${props.permittedAnswer.answerId}-dropdown`];

  switch (props.permittedAnswer.renderingConfig.type) {
    case SurveyComponentTypeEnum.checkbox:
      value = checkedValue ? checkedValue : undefined;
      break;
    case SurveyComponentTypeEnum.radio:
    case SurveyComponentTypeEnum.iconRadio:
      value = parseInt(radioValue as string, 10) === props.permittedAnswer.answerId || undefined;
      break;
    case SurveyComponentTypeEnum.textbox:
      value = textValue ? textValue : undefined;
      break;
    case SurveyComponentTypeEnum.number:
      value = typeof (numberValue) == 'number' ? numberValue : undefined;
      break;
    case SurveyComponentTypeEnum.dropdown:
      value = dropdownValue ? dropdownValue : undefined;
  }
  return value;
});

const isDisabledByExclusiveChoice = computed(() => {
  const selectedValues = syncValue.value[props.depth] as Record<string, unknown>;
  return props.question.permittedAnswers.some(pa =>
    pa.renderingConfig.exclusiveChoice &&
    pa.answerId !== props.permittedAnswer.answerId &&
    !!selectedValues[`${props.question.questionId}-${pa.answerId}-checked`]
  );
});

function onCheckboxChanged() {
  const selectedValues = syncValue.value[props.depth] as Record<string, unknown>;
  const currentValueKey = `${props.question.questionId}-${props.permittedAnswer.answerId}-checked`;
  const isChecked = selectedValues[currentValueKey];

  //if an exclusive none option exists, it's behavior is "checkability" is mutually exclusive to all other checboxes in its depth
  //If we are checking an exclusive none anwswer, change all other answers to not checked
  //If we are checking a regular answer, change the exlcusive none answer to not checked
  const isExclusiveNoneAnswerChecked = props.permittedAnswer.renderingConfig.exclusiveChoice;

  //behavior remove any granchildren answers that may have been given prior to choosing an Exclusive None answer
  //reason: prevents secret submission of child answers if they were specified previously for regular checkbox parents that are no longer selected
  if (isExclusiveNoneAnswerChecked) {
    const childrenState = syncValue.value[props.depth + 1] as Record<string, unknown>;
    for (const key in childrenState) {
      childrenState[key] = undefined;
    }
  }

  //behavior: clean up any descendeant children state since props parent was unselected
  //reason: prevents secret submission of child answers if they were specified previously
  if (!isChecked && props.permittedAnswer.subAnswers?.length) {
    const childrenState = syncValue.value[props.depth + 1] as Record<string, unknown>;
    props.permittedAnswer.subAnswers.forEach((subItem) => {
      if (childrenState[`${subItem.answerId}-text`]) {
        childrenState[`${subItem.answerId}-text`] = undefined;
      }
      if (childrenState[`${props.question.questionId}-${subItem.answerId}-checked`]) {
        childrenState[`${props.question.questionId}-${subItem.answerId}-checked`] = undefined;
      }
      if (childrenState[`${props.question.questionId}-number`]) {
        childrenState[`${props.question.questionId}-number`] = undefined;
      }
      if (childrenState[`${subItem.answerId}-dropdown`]) {
        childrenState[`${subItem.answerId}-dropdown`] = undefined;
      }
    });
  }

  //behavior: props section handles special checkmark behavior between regular checkboxes and a ExclusiveNone checkbox.
  //reason: if you select an ExclusiveNone checkbox, unselect the rest and if you have ExclusiveNone checkbox selected then selected a regular checkbox, then
  //        unselect the ExclusiveNone
  for (const key in selectedValues) {
    if (isChecked && key !== currentValueKey && key.endsWith('-checked')) {
      // Key format: ${questionId}-${answerId}-checked — scope to the current question only
      const keyParts = key.split('-');
      const keyQuestionId = parseInt(keyParts[0]);
      if (keyQuestionId !== props.question.questionId) continue;

      const keyAnswerId = parseInt(keyParts[1]);
      const siblingAnswer = surveyModule.allPermittedAnswersOnCurrentPage.find(
        (pa) => pa.answerId === keyAnswerId
      );
      if (isExclusiveNoneAnswerChecked || siblingAnswer?.renderingConfig.exclusiveChoice) {
        selectedValues[key] = false;
      }
    }
  }

  //behavior: when exclusive-none is checked, clear text/number/dropdown for all sibling answers at this depth
  //reason: the loop above only finds siblings with a -checked key; plain text box answers have no -checked key and would otherwise retain their stale value
  if (isExclusiveNoneAnswerChecked && isChecked) {
    props.question.permittedAnswers.forEach(pa => {
      if (pa.answerId === props.permittedAnswer.answerId) return;
      selectedValues[`${pa.answerId}-text`] = undefined;
      selectedValues[`${pa.answerId}-dropdown`] = undefined;
    });
    selectedValues[`${props.question.questionId}-number`] = undefined;
  }
}

function onRadioChanged() {
  const selectedValues = syncValue.value[props.depth];
  const currentValueKey = `${props.permittedAnswer.renderingConfig.groupIdentifier}`;
  const currentValue = selectedValues[currentValueKey];
  const permittedAnswer = props.permittedAnswer;

  const siblingPermittedAnswers = surveyModule.allPermittedAnswersOnCurrentPage.filter(
    (pa) =>
      pa.parentAnswerId === permittedAnswer.parentAnswerId &&
      pa.answerId.toString() !== currentValue
  );

  //behavior: clean up any descendent children state since props parent was unselected
  //reason: prevents secret submission of child answers if they were specified previously
  siblingPermittedAnswers.forEach((sibling) => {
    if (sibling.subAnswers?.length) {
      const childrenState = syncValue.value[props.depth + 1] as Record<string, unknown>;
      sibling.subAnswers.forEach((subItem) => {
        if (childrenState[`${subItem.answerId}-text`]) {
          childrenState[`${subItem.answerId}-text`] = undefined;
        }
        if (childrenState[`${props.question.questionId}-${subItem.answerId}-checked`]) {
          childrenState[`${props.question.questionId}-${subItem.answerId}-checked`] = undefined;
        }
        if (childrenState[`${props.question.questionId}-number`]) {
          childrenState[`${props.question.questionId}-number`] = undefined;
        }
        if (childrenState[`${subItem.answerId}-dropdown`]) {
          childrenState[`${subItem.answerId}-dropdown`] = undefined;
        }
      });
    }
  });
}

async function validate() {
  let valid = true
  //only validate children if the "validateChildren property's value matches the current value of the component"
  if (
    multipleChoiceSubAnswers.value &&
    (typeof props.permittedAnswer.renderingConfig.validateChildrenIfValueMatches === 'undefined' ||
      props.permittedAnswer.renderingConfig.validateChildrenIfValueMatches === value.value)
  ) {
    const results: boolean[] = await Promise.all(
      multipleChoiceSubAnswers.value.map((mc: any) => mc.validate())
    );
    valid = valid && results.every((childIsValid) => childIsValid);
  }

  //validate on text input component
  if (props.permittedAnswer?.renderingConfig?.type === SurveyComponentTypeEnum.textbox) {
    const actualValue = syncValue.value?.[props.depth]?.[`${props.permittedAnswer.answerId}-text`];
    if (actualValue && inputRef?.value?.validate)
      valid = inputRef.value.validate();
  }

  return valid;
}

function getResponses(): SurveyProgressAnswer[] {
  const data = {
    question: props.question,
    permittedAnswer: props.permittedAnswer,
    value: value.value,
  };

  if (multipleChoiceSubAnswers.value) {
    const subChoiceResponses = multipleChoiceSubAnswers.value
      .map((mca: any) => mca.getResponses())
      .flat();
    return [data, ...subChoiceResponses];
  }

  return [data];
}

defineExpose({
  validate,
  getResponses,
});
</script>

<template>
  <div :class="[
    permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.heading ||
      permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.label
      ? 'survey__answer-group'
      : 'survey__answer-option',
    { footer: permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.footer },
    { 'golden-jubilee-vips-radio': permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.iconRadio },
  ]">

    <!-- Section (heading) -->
    <template v-if="permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.heading">
      <h4 class="survey__answer-heading">{{ permittedAnswer.name }}</h4>
    </template>
    <template v-if="permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.label">
      <div class="survey__answer-label">{{ permittedAnswer.name }}</div>
    </template>
    <template v-if="permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.footer">
      <span class="italic">{{ permittedAnswer.name }}</span>
    </template>

    <!-- Checkbox Input -->
    <template v-if="permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.checkbox">
        <Checkbox v-model="syncValue[depth][`${question.questionId}-${permittedAnswer.answerId}-checked`]"
                  @update:modelValue="onCheckboxChanged"
                  :class="[{'q-checkbox--no-checkbox-disable' : props.question?.questionCategory === QuestionCategoryResponseEnum.MultipleChoiceConsent && permittedAnswer.renderingConfig.disabled}]"
                  :variant="props.question?.questionCategory === QuestionCategoryResponseEnum.MultipleChoiceConsent ? undefined : $zb.enums.CustomCheckboxVariantEnum.survey"
                  :disable="permittedAnswer.renderingConfig.disabled"
                  :label="permittedAnswer.name" />
    </template>

    <!-- Radio Input -->
    <template v-if="permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.radio">
        <Radio v-model="syncValue[depth][permittedAnswer.renderingConfig.groupIdentifier]"
               @update:modelValue="onRadioChanged"
               :id="`${question.questionId}-${permittedAnswer.answerId}`"
               :label="permittedAnswer.name"
               :name="permittedAnswer.renderingConfig.groupIdentifier"
               :radio-variation="$zb.enums.CustomRadioVariantEnum.survey"
               :val="permittedAnswer.answerId.toString()" />
    </template>

    <!-- Golden Jubilee VIPs Radio Input -->
    <template v-if="permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.iconRadio">
      <IconValNmSubtextBtnAsRadio v-model="syncValue[props.depth][props.permittedAnswer.renderingConfig.groupIdentifier]"
                                 :permitted-ans="props.permittedAnswer"
                                 :q-id="props.question?.questionId" />
    </template>

    <!-- Text Input -->
    <template v-if="permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.textbox">
      <InputWithValidation v-model="syncValue[depth][`${permittedAnswer.answerId}-text`]" ref="inputRef"
                           :for="permittedAnswer.renderingConfig.vid"
                           :input-width="permittedAnswer.renderingConfig.range.max > 5 ? $zb.enums.FormElWidthEnum.qFormEl300 : $zb.enums.FormElWidthEnum.qFormEl100"
                           :label="t(permittedAnswer.renderingConfig.labelKey).toString() || ''"
                           :placeholder="permittedAnswer.renderingConfig.placeholder !== null ? permittedAnswer.renderingConfig.placeholder : ''"
                           :rules="permittedAnswer.renderingConfig.validationRules"
                           :disable="isDisabledByExclusiveChoice" />
    </template>

    <!-- Number Input -->
    <template v-if="permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.number">
      <NumberInputWithValidation v-model="syncValue[depth][`${question.questionId}-number`]" ref="numberInputRef"
                                 class="survey__number"
                                 :for="permittedAnswer.renderingConfig.vid"
                                 :label="t(permittedAnswer.renderingConfig.labelKey).toString() || ''"
                                 :input-width="permittedAnswer.renderingConfig.range.max > 100 ? $zb.enums.FormElWidthEnum.qFormEl300 : $zb.enums.FormElWidthEnum.qFormEl125"
                                 :max="permittedAnswer.renderingConfig.range?.max"
                                 :min="permittedAnswer.renderingConfig.range?.min"
                                 :placeholder="permittedAnswer.renderingConfig.range.min + ' - ' + permittedAnswer.renderingConfig.range.max"
                                 :step="permittedAnswer.renderingConfig.range.scale ?? 1"
                                 :disable="isDisabledByExclusiveChoice" />
      <div class="help-text">{{ typeof (syncValue[depth][`${question.questionId}-number`]) == 'number' ? '&nbsp;' :
    t('survey_range_tap') }}</div>
    </template>

    <!-- Select Input -->
    <template v-if="permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.dropdown">
      <SelectWithValidation v-model="syncValue[depth][`${permittedAnswer.answerId}-dropdown`]"
                            :default-option-text="permittedAnswer.renderingConfig.dropdownText"
                            option-label="text"
                            :options="permittedAnswer.renderingConfig.dropdownItems"
                            option-value="value"
                            :disable="isDisabledByExclusiveChoice" />
    </template>

    <!-- SubText -->
    <div v-if="showSubText" class="survey__answer-subtext">{{ permittedAnswer.subText }}</div>

    <!-- Sub-Choice -->
    <TheSurveyMultipleChoice v-show="showOptions"
                             v-for="subChoice in permittedAnswer.subAnswers"
                             v-model="syncValue"
                             :class="[{ 'last-tier': !subChoice.subAnswers }]"
                             :depth="depth + 1"
                             :key="subChoice.answerId"
                             :permittedAnswer="subChoice"
                             :question="question"
                             ref="multipleChoiceSubAnswers" />
  </div>
</template>

<style scoped lang="scss">
@use 'sass:map';

.survey__answer {
  $survey-answer-option-brdr: 1px solid $border-clr;

  // Option
  > .survey__answer-option:not(.footer),
  .survey__answer-group > .survey__answer-option {
    padding-block: 20px;
    border-block-end: $survey-answer-option-brdr;
  }

  // First Option
  .survey__answer-option:first-child,
  .survey__answer-group > .survey__answer-option:first-child,
  .survey__answer-group h4 + .survey__answer-option {
    border-block-start: $survey-answer-option-brdr;
  }

  // Option Inside an Option (sub-option)
  > .survey__answer-option .survey__answer-option,
  .survey__answer-group > .survey__answer-option .survey__answer-option {
    padding-block-end: 15px;
    
    &:last-child {
      padding-block-end: 0;
    }
    
    &.last-tier {
      padding-inline-start: 50px;
    }
  }
  
  // Subtext
  &-subtext {
    font-size: map.get($font-sizes, '16');
    line-height: 24px;
    color: $content-txt-secondary;
    padding-block-start: 5px;
    // align with the checkbox/radio label text: 35px control + 15px label padding
    padding-inline-start: 50px;
  }

  // Option Inside an Option (sub-option);
  // immediately following the parent options element or subtext.
  > .survey__answer-option {

    :deep(.q-radio),
    .survey__answer-subtext {

      + .survey__answer-option {
        padding-block-start: 15px;
      }
    }
  }
  
  // Golden Jubilee Vips Radio Option
  
  > .survey__answer-option.golden-jubilee-vips-radio {
    padding-block: 0;
    border-block: none;
    
    &:not(:last-child) {
      margin-block-end: 12px;
    }
  }

  // Group
  &-group {
    margin-block-end: 25px;
    
    // Heading
    .survey__answer-heading {
      color: $body-txt-clr;
    }

    .survey__answer-label {
      font-size: map.get($font-sizes, '22');
      margin-block-start: 25px;
    }
  }
}

.help-text {
  font-style: italic;
  font-weight: map.get($text-weights, bold);
  font-size: 26px;
  line-height: 56px;
  color: map.get($co-brand-clrs, primitives-foundation-50);
  text-align: center;
}
</style>
